import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import { apolloClient } from './app/core/graphql';
import { store } from './app/store';
import { ToastProvider } from './app/modules/shared/components/Toast';
import { AuthProvider } from './app/core/auth/AuthProvider';
import { initializeMsal } from './app/core/auth/msalConfig';
import { assertMockAuthNotInProduction } from './app/core/auth/mockAuthGuard';
import { environment } from './app/core/config/environment';
import './styles/globals.css';
import App from './App.tsx';

// Fail fast, before anything renders: a production bundle must never run with mock auth.
assertMockAuthNotInProduction(import.meta.env.PROD, environment.mockAuth);

// Auto-recover from stale code-split chunks after a new deployment. A tab left open on an older
// build still references the previous build's hashed chunk filenames, so navigating to a lazily
// imported route 404s ("Failed to fetch dynamically imported module"). Vite fires
// `vite:preloadError` in exactly that case — reload once to pick up the new index.html + chunks.
// A short time-boxed guard avoids a reload loop if the assets are genuinely unavailable.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'chunk-reload-at';
  const now = Date.now();
  const last = Number(sessionStorage.getItem(KEY) ?? '0');
  if (now - last < 10_000) return;
  sessionStorage.setItem(KEY, String(now));
  window.location.reload();
});

// MSAL v3 requires initialize() + redirect handling to complete before render.
// No-op when B2C is disabled (mock/dev).
initializeMsal().finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AuthProvider>
        <ApolloProvider client={apolloClient}>
          <Provider store={store}>
            <ToastProvider>
              <App />
            </ToastProvider>
          </Provider>
        </ApolloProvider>
      </AuthProvider>
    </StrictMode>
  );
});
